import React, { useState, useMemo } from 'react';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification, formatDateFR } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

export default function MokhalafatForm({ mokhalafa, rentals = [], clients = [], cars = [], settings, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { add, update } = useCollection('mokhalafat');

  const [data, setData] = useState(mokhalafa || {
    // Avis info
    avisNumber: '',          // N° avis NARSA (numeric)
    avisRF: '',              // RF code
    // Person filing (déclarant = agence)
    declarantType: 'morale', // 'physique' | 'morale'
    declarantNom: settings.companyName || '',
    declarantPrenom: '',
    declarantFonction: 'Gérant',
    declarantGsm: settings.companyPhone || '',
    declarantEmail: settings.companyEmail || '',
    declarantRaisonSociale: settings.companyName || '',
    declarantAdresse: settings.companyAddress || '',
    declarantVille: settings.companyCity || '',
    declarantCodePostal: '',
    // Vehicle
    rentalId: '',
    carId: '',
    carName: '',
    carPlate: '',
    // Driver (auteur infraction)
    clientId: '',
    driverType: 'physique',  // 'physique' | 'morale'
    clientName: '',
    clientFirstName: '',
    clientAddress: '',
    clientCity: '',
    clientPostalCode: '',
    clientCin: '',
    clientPermis: '',
    // If morale (rare)
    moraleRaisonSociale: '',
    moraleAdresse: '',
    moraleVille: '',
    moralePostalCode: '',
    // Status
    infractionDate: '',
    infractionType: '',
    infractionAmount: 0,
    status: 'pending',
    notes: '',
    fait_a: settings.companyCity || '',  // Lieu de déclaration
    le_date: new Date().toISOString().split('T')[0],
    clientSignature: '',
    signedAt: ''
  });
  const [searchRental, setSearchRental] = useState('');

  const selectRental = (rental) => {
    const client = clients.find(c => c.id === rental.clientId || c._id === rental.clientId);
    const car = cars.find(c => c.id === rental.carId || c._id === rental.carId);
    const fullName = rental.clientName || (client && client.name) || '';
    const parts = fullName.trim().split(/\s+/);
    
    setData({
      ...data,
      rentalId: rental.id,
      clientId: rental.clientId || (client && client.id) || '',
      clientName: parts[0] || fullName,                    // Nom (first word)
      clientFirstName: parts.slice(1).join(' ') || '',     // Prénoms (rest)
      clientCin: rental.clientCin || (client && client.cin) || '',
      clientPermis: rental.clientPermis || (client && client.permis) || '',
      clientAddress: rental.clientAddress || (client && client.address) || '',
      clientCity: (client && client.city) || '',
      carId: rental.carId || (car && car.id) || '',
      carName: rental.carName || (car && `${car.brand} ${car.model}`) || '',
      carPlate: rental.carPlate || (car && car.plate) || ''
    });
    setSearchRental('');
  };

  const filteredRentals = useMemo(() => {
    if (!searchRental.trim()) return rentals.slice(0, 5);
    const q = searchRental.toLowerCase();
    return rentals.filter(r =>
      (r.clientName || '').toLowerCase().includes(q) ||
      (r.carName || '').toLowerCase().includes(q) ||
      (r.carPlate || '').toLowerCase().includes(q) ||
      (r.clientCin || '').toLowerCase().includes(q)
    ).slice(0, 10);
  }, [rentals, searchRental]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!data.clientName || !data.carPlate) {
      return alert(isAr ? '⚠️ معلومات العميل والسيارة مطلوبة' : '⚠️ Infos client et voiture requises');
    }
    const payload = { ...data, infractionAmount: parseFloat(data.infractionAmount) || 0 };
    try {
      if (mokhalafa?.id) {
        await update(mokhalafa.id, payload);
      } else {
        payload.createdAt = new Date().toISOString();
        await add(payload);
        await sendTelegramNotification(
          buildTelegramMessage('contravention', {
            carName: data.carName, carPlate: data.carPlate,
            clientName: data.clientName, clientFirstName: data.clientFirstName,
            date: formatDateFR(data.infractionDate), avisNumber: data.avisNumber,
            amount: data.infractionAmount, currency: settings.currency || 'MAD'
          }),
          'contravention'
        );
      }
      onClose();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title={mokhalafa
        ? (isAr ? '✏️ تعديل المخالفة' : '✏️ Modifier la contravention')
        : (isAr ? '➕ مخالفة جديدة' : '➕ Nouvelle contravention')}
    >
      <form onSubmit={submit}>
        {/* Step 1: Select rental */}
        {!data.rentalId && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <h3 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-1">
              🔍 {isAr ? 'اختر عقد الإيجار:' : 'Sélectionner le contrat de location:'}
            </h3>
            <input
              type="text"
              value={searchRental}
              onChange={e => setSearchRental(e.target.value)}
              placeholder={isAr ? 'ابحث...' : 'Recherche par nom, voiture, plaque...'}
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm mb-2 focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {filteredRentals.length === 0 ? (
              <p className="text-xs text-gray-500 italic text-center py-2">
                {isAr ? 'لا توجد نتائج' : 'Aucun résultat'}
              </p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {filteredRentals.map(r => (
                  <button
                    key={r.id} 
                    type="button"
                    onClick={() => selectRental(r)}
                    className="w-full text-start p-2 bg-white hover:bg-blue-100 border border-gray-200 rounded text-xs transition"
                  >
                    <div className="font-bold text-gray-900">{r.clientName}</div>
                    <div className="text-gray-600 font-mono">
                      {`🚗 ${r.carName} • 📅 ${formatDateFR(r.startDate)}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-blue-700 mt-2 italic">
              {isAr ? '💡 أو املأ يدوياً' : '💡 Ou remplissez manuellement'}
            </p>
          </div>
        )}

        {data.rentalId && (
          <div className="mb-4 p-3 bg-green-50 border border-green-350 rounded-lg flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-green-900">
                ✓ {isAr ? 'مرتبط بعقد:' : 'Lié au contrat:'}
              </div>
              <div className="text-xs text-green-700 font-mono mt-0.5">
                {data.clientName} {data.clientFirstName} • {data.carName}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setData({ ...data, rentalId: '' })}
              className="text-xs text-red-650 hover:bg-red-50 px-2 py-1 rounded transition-colors"
            >
              {isAr ? 'إلغاء' : 'Délier'}
            </button>
          </div>
        )}

        {/* ===== Section 1: AVIS NARSA ===== */}
        <div className="mb-4 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl">
          <h3 className="text-sm font-bold text-amber-900 mb-2">
            📄 {isAr ? 'إشعار المخالفة من NARSA' : 'Avis de contravention NARSA'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field
              label={isAr ? 'رقم الإشعار' : 'N° Avis'}
              value={data.avisNumber}
              onChange={v => setData({ ...data, avisNumber: v })}
              placeholder="Ex: 123456789"
            />
            <Field
              label="Code RF"
              value={data.avisRF}
              onChange={v => setData({ ...data, avisRF: v })}
              placeholder="RF"
            />
            <Field
              label={isAr ? 'تاريخ المخالفة' : 'Date infraction'}
              type="date"
              value={data.infractionDate}
              onChange={v => setData({ ...data, infractionDate: v })}
            />
          </div>
        </div>

        {/* ===== Section 2: VEHICLE ===== */}
        <h3 className="text-sm font-bold text-gray-700 mb-2 mt-3">
          🚗 {isAr ? 'المركبة' : 'Véhicule'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={isAr ? 'الماركة والموديل' : 'Marque & Modèle'}
            value={data.carName}
            onChange={v => setData({ ...data, carName: v })}
          />
          <Field
            label={isAr ? 'رقم التسجيل' : 'Plaque'}
            value={data.carPlate}
            onChange={v => setData({ ...data, carPlate: v })}
          />
        </div>

        {/* ===== Section 3: AUTEUR (CLIENT) ===== */}
        <h3 className="text-sm font-bold text-gray-700 mb-2 mt-3 pt-3 border-t border-gray-200">
          👤 {isAr ? 'مرتكب المخالفة (السائق)' : "Auteur de l'infraction (Conducteur)"}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={isAr ? 'الاسم العائلي' : 'Nom'}
            value={data.clientName}
            onChange={v => setData({ ...data, clientName: v })}
          />
          <Field
            label={isAr ? 'الاسم الشخصي' : 'Prénom(s)'}
            value={data.clientFirstName}
            onChange={v => setData({ ...data, clientFirstName: v })}
          />
        </div>
        <div className="mb-3">
          <Field
            label={isAr ? 'العنوان' : 'Adresse'}
            value={data.clientAddress}
            onChange={v => setData({ ...data, clientAddress: v })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={isAr ? 'المدينة' : 'Ville'}
            value={data.clientCity}
            onChange={v => setData({ ...data, clientCity: v })}
          />
          <Field
            label={isAr ? 'الرمز البريدي' : 'Code postal'}
            value={data.clientPostalCode}
            onChange={v => setData({ ...data, clientPostalCode: v })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={isAr ? 'رقم CIN' : 'N° CIN'}
            value={data.clientCin}
            onChange={v => setData({ ...data, clientCin: v })}
          />
          <Field
            label={isAr ? 'رقم رخصة السياقة' : 'N° Permis'}
            value={data.clientPermis}
            onChange={v => setData({ ...data, clientPermis: v })}
          />
        </div>

        {/* ===== Section 4: STATUS ===== */}
        <h3 className="text-sm font-bold text-gray-700 mb-2 mt-3 pt-3 border-t border-gray-200">
          📋 {isAr ? 'تفاصيل إضافية' : 'Détails supplémentaires'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={isAr ? 'نوع المخالفة' : "Type d'infraction"}
            value={data.infractionType}
            onChange={v => setData({ ...data, infractionType: v })}
          />
          <Field
            label={isAr ? 'المبلغ' : 'Montant (MAD)'}
            type="number"
            value={data.infractionAmount}
            onChange={v => setData({ ...data, infractionAmount: v })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={isAr ? 'مكان التحرير' : 'Fait à (lieu)'}
            value={data.fait_a}
            onChange={v => setData({ ...data, fait_a: v })}
          />
          <Field
            label={isAr ? 'تاريخ التحرير' : 'Le (date)'}
            type="date"
            value={data.le_date}
            onChange={v => setData({ ...data, le_date: v })}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            {'📊 ' + (isAr ? 'الحالة' : 'Statut')}
          </label>
          <select
            value={data.status}
            onChange={e => setData({ ...data, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
          >
            <option value="pending">⏳ {isAr ? 'في الانتظار' : 'En attente'}</option>
            <option value="transferred">✅ {isAr ? 'محولة إلى NARSA' : 'Transférée à NARSA'}</option>
            <option value="paid">💰 {isAr ? 'مدفوعة' : 'Payée par client'}</option>
            <option value="cancelled">❌ {isAr ? 'ملغاة' : 'Annulée'}</option>
          </select>
        </div>

        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold"
          >
            {isAr ? 'إلغاء' : 'Annuler'}
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-bold"
          >
            💾 {isAr ? 'حفظ' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
